

// FIX: Correctly import all necessary types from `domain/entities`.
import { TeamInLeague, TacticalMentality, Player } from '../domain/entities';

interface TeamComposition {
    attack: number;
    mid: number;
    def: number;
}

// A simplified version of the main simulator's composition logic for speed.
const getTeamCompositionForPrediction = (team: TeamInLeague): TeamComposition => {
    const availablePlayers = team.squad.filter(p => p.injury?.type !== 'Severe');
    if (availablePlayers.length < 11) {
        // Assume a heavy penalty for not fielding a full team
        return { attack: team.baseSkill * 0.7, mid: team.baseSkill * 0.7, def: team.baseSkill * 0.7 };
    }
    
    // Quick average of the top 11 available players
    const top11 = availablePlayers.sort((a,b) => b.skill - a.skill).slice(0, 11);
    
    const strengths = { attack: 0, mid: 0, def: 0, gk: 0 };
    const playerCount = { attack: 0, mid: 0, def: 0, gk: 0 };

    top11.forEach(player => {
        const effectiveSkillWithForm = player.skill + player.form;
        const effectiveSkill = player.injury?.type === 'Light' ? effectiveSkillWithForm * 0.8 : effectiveSkillWithForm;
        if (player.position === 'GK') {
             strengths.gk += effectiveSkill;
             playerCount.gk++;
        } else if (player.position === 'DEF') {
             strengths.def += effectiveSkill;
             playerCount.def++;
        } else if (player.position === 'MID') {
             strengths.mid += effectiveSkill;
             playerCount.mid++;
        } else if (player.position === 'FWD') {
            strengths.attack += effectiveSkill;
            playerCount.attack++;
        }
    });
    
    const defRating = ((playerCount.def > 0 ? strengths.def / playerCount.def : team.baseSkill) + (playerCount.gk > 0 ? strengths.gk / playerCount.gk : team.baseSkill * 0.8))/2;

    return {
        attack: playerCount.attack > 0 ? strengths.attack / playerCount.attack : team.baseSkill * 0.9,
        mid: playerCount.mid > 0 ? strengths.mid / playerCount.mid : team.baseSkill,
        def: defRating,
    };
};

const applyMentality = (composition: TeamComposition, mentality: TacticalMentality) => {
    const newComposition = { ...composition };
    switch (mentality) {
        case 'Attacking':
            newComposition.attack *= 1.1;
            newComposition.def *= 0.9;
            break;
        case 'Defensive':
            newComposition.attack *= 0.9;
            newComposition.def *= 1.1;
            break;
    }
    return newComposition;
};

const runSingleSimulation = (homeTeam: TeamInLeague, awayTeam: TeamInLeague): { homeScore: number, awayScore: number } => {
    let homeComposition = getTeamCompositionForPrediction(homeTeam);
    let awayComposition = getTeamCompositionForPrediction(awayTeam);
    
    homeComposition = applyMentality(homeComposition, homeTeam.tactics.mentality);
    awayComposition = applyMentality(awayComposition, awayTeam.tactics.mentality);

    const homeStrength = {
        attack: homeComposition.attack + 2,
        mid: homeComposition.mid + 2,
        def: homeComposition.def + 2,
    };
    const awayStrength = { ...awayComposition };

    const homeAttackVsAwayDef = homeStrength.attack - awayStrength.def;
    const awayAttackVsHomeDef = awayStrength.attack - homeStrength.def;
    const homeMidAdvantage = homeStrength.mid - awayStrength.mid;

    const midControlFactor = Math.max(0.2, Math.min(0.8, 0.5 + (homeMidAdvantage / 20)));
    const totalChances = 15 + Math.floor(Math.random() * 10);
    const homeChances = Math.round(totalChances * midControlFactor);
    const awayChances = totalChances - homeChances;

    const homeConversionRate = Math.max(0.02, Math.min(0.3, 0.08 + (homeAttackVsAwayDef / 250)));
    const awayConversionRate = Math.max(0.02, Math.min(0.3, 0.07 + (awayAttackVsHomeDef / 250)));
    
    let homeScore = 0;
    for (let i = 0; i < homeChances; i++) {
        if (Math.random() < homeConversionRate) homeScore++;
    }
    
    let awayScore = 0;
    for (let i = 0; i < awayChances; i++) {
        if (Math.random() < awayConversionRate) awayScore++;
    }

    return { homeScore, awayScore };
};

export class MatchPredictionService {
    static calculateProbabilities(homeTeam: TeamInLeague, awayTeam: TeamInLeague): { homeProb: number, drawProb: number, awayProb: number } {
        const SIMULATIONS = 200;
        let homeWins = 0;
        let draws = 0;

        for (let i = 0; i < SIMULATIONS; i++) {
            const result = runSingleSimulation(homeTeam, awayTeam);
            if (result.homeScore > result.awayScore) {
                homeWins++;
            } else if (result.homeScore === result.awayScore) {
                draws++;
            }
        }

        const homeProb = homeWins / SIMULATIONS;
        const drawProb = draws / SIMULATIONS;
        const awayProb = 1 - homeProb - drawProb;

        return { homeProb, drawProb, awayProb };
    }

    static getExpectedResult(homeTeam: TeamInLeague, awayTeam: TeamInLeague): string {
        const { homeProb, drawProb, awayProb } = this.calculateProbabilities(homeTeam, awayTeam);
        const probs = [
            { outcome: 'Home Win', prob: homeProb },
            { outcome: 'Draw', prob: drawProb },
            { outcome: 'Away Win', prob: awayProb },
        ];
        
        probs.sort((a, b) => b.prob - a.prob);
        
        const highestProb = probs[0];
        const secondHighestProb = probs[1];

        // If the top two probabilities are very close, it's too close to call
        if (highestProb.prob - secondHighestProb.prob < 0.05) {
            return "Too close to call";
        }

        return highestProb.outcome;
    }
}